import { useEventListener, useFocusWithin } from "@vueuse/core";
import { watch } from "vue";

import { useHostEmit } from "#ce/composables/useHostEmit";
import { stripNonDigits } from "#ce/utils/input-masks";

/**
 * Turns what the user does to the boxes into digits and events. Typing a digit fills the box and
 * moves on, deleting empties it, and anything else is put back. `input` fires per edit, `complete`
 * once every box holds a digit, and `change` when focus leaves the group after an edit that
 * changed the value. Script writes and form resets report nothing.
 *
 * @param {object} options
 * @param {import("vue").Ref<HTMLElement|null>} options.group - Element holding the boxes.
 * @param {import("vue").ComputedRef<number>} options.count - Number of boxes.
 * @param {import("vue").Ref<string[]>} options.digits - The digit of every box.
 * @param {import("vue").ComputedRef<string>} options.value - The joined digits.
 * @param {(index: number, digit: string) => void} options.setDigit - Writes one digit.
 * @param {(event: Event) => number} options.boxIndex - Index of the box an event came from.
 * @param {(index: number, wasNextBoxEmpty: boolean) => void} options.focusAfterTyping - Advances focus after a digit.
 * @param {(candidate: *) => string} options.parseValue - Returns a candidate that fits, otherwise an empty string.
 * @param {(candidate: *) => void} options.writeUserValue - Writes the digits as a user edit.
 * @param {(value: string) => void} options.emitComplete - Announces a full passcode.
 * @returns {{ reportUserEdit: () => void, resetEditTracking: () => void }}
 */
export function useOtpUserEdits({
  group,
  count,
  digits,
  value,
  setDigit,
  parseValue,
  writeUserValue,
  boxIndex,
  focusAfterTyping,
  emitComplete,
}) {
  const { emitBubbling } = useHostEmit();
  const { focused } = useFocusWithin(group);

  let editedSinceFocus = false;
  let valueAtFocus = "";

  function resetEditTracking() {
    editedSinceFocus = false;
    valueAtFocus = value.value;
  }

  function reportUserEdit() {
    editedSinceFocus = true;
    emitBubbling("input", value.value);

    if (value.value.length === count.value) {
      emitComplete(value.value);
    }
  }

  function reportChangeOnBlur() {
    if (editedSinceFocus && value.value !== valueAtFocus) {
      emitBubbling("change", value.value);
    }

    editedSinceFocus = false;
  }

  function handleFocusWithinChange(isFocused) {
    if (isFocused) {
      resetEditTracking();
    } else {
      reportChangeOnBlur();
    }
  }

  function handleInput(event) {
    // The native `input` stops here: the host reports its own through `emitBubbling`.
    event.stopPropagation();

    const index = boxIndex(event);

    if (event.inputType === "deleteContentBackward") {
      setDigit(index, "");
      reportUserEdit();
      return;
    }

    const inserted = stripNonDigits(event.data ?? event.target.value);
    const code = parseValue(inserted);

    if (inserted.length > 1 && code !== "") {
      writeUserValue(code);
      reportUserEdit();
      event.target.blur();
      return;
    }

    const typed = inserted.at(-1);

    if (typed === undefined) {
      event.target.value = digits.value[index];
      return;
    }

    const wasNextBoxEmpty = digits.value[index + 1] === "";

    setDigit(index, typed);
    event.target.value = typed;
    reportUserEdit();
    focusAfterTyping(index, wasNextBoxEmpty);
  }

  useEventListener(group, "input", handleInput);
  watch(focused, handleFocusWithinChange);

  return { reportUserEdit, resetEditTracking };
}
